// THE OPERATIONS INBOX'S SCRIPT CARDS, AS PURE READINGS: what a review-scripts
// or overdue-scripts card owes, from the rows the throughput page's own
// operations list is made of. No database: lib/server/ops-inbox-scripts.ts
// reads the rows and the actions, and the tick hands each card's reading to
// `stepOwed` (lib/ops-inbox-cards.ts).
//
// ⚠️ THE ACTIONS ARE THE SIGNAL, NEVER A RULE OF OUR OWN. Which script awaits
// the AM and which deliverable is late is `operationsActions()`'s answer (the
// Throughput page's "Waiting for AM" and "Not submitted — overdue"), so a card
// can't disagree with that page. What this adds is only the grouping: a week
// of one creator's scripts for one brand (review), every late week of one
// creator for one brand, on both ladders (overdue).
//
// ⚠️ FOOTAGE WAITING FOR THE AM IS NOT A SCRIPT REVIEW. The footage ladder has
// `am-review` actions too; review scripts reads the script ladder's alone.
//
// Shared, not server-only, and erasable TypeScript: the tick runs in a sidecar
// under node's type stripping.

import type { CardDetail, CardRow, OwedReading } from './ops-inbox-cards';
import { subjectFor } from './ops-inbox-kinds';
import { addDaysUtc, daysBetweenUtc } from './schedule-dates';
import { operationsDay as operationsDayOf, operationsDayStart, type OperationsAction } from './throughput-operations';
import type { WorkloadRow } from './throughput-workload';

/** One script on a review card: what its pill says. `done` 1 once it has left the AM's review. */
export interface ReviewScript {
  id: string;
  title: string;
  /** The latest grade, 0–100; null for a script never graded. */
  grade: number | null;
  done: 0 | 1;
}

/** A week of one creator's scripts for one brand, and what the card is keyed by. */
export interface ReviewWeek {
  subject: string;
  brand: string;
  creator: string;
  /** The scripts' own week number (the week key's last part). */
  week: number;
  /** The week's scripts awaiting the AM now. */
  awaiting: OperationsAction[];
}

export interface LateCreator {
  subject: string;
  brand: string;
  creator: string;
  /** Every late deliverable of theirs for the brand, both ladders, every week. */
  late: OperationsAction[];
}

const isScriptReview = (a: OperationsAction): boolean => a.reason === 'am-review' && a.ladder === 'script';

/** The subject of the review card a script's week belongs to. */
export const reviewSubjectOf = (row: Pick<WorkloadRow, 'creatorEmail' | 'brandSlug' | 'week'>): string =>
  subjectFor('review-scripts', { creator: row.creatorEmail, brand: row.brandSlug, week: String(row.week) });

/** The subject of a creator's overdue card for a brand. */
export const overdueSubjectOf = (row: Pick<WorkloadRow, 'creatorEmail' | 'brandSlug'>): string =>
  subjectFor('overdue-scripts', { creator: row.creatorEmail, brand: row.brandSlug });

/** The weeks with scripts awaiting the AM, by card subject. */
export function reviewWeeks(actions: readonly OperationsAction[]): Map<string, ReviewWeek> {
  const out = new Map<string, ReviewWeek>();
  for (const a of actions) {
    if (!isScriptReview(a)) continue;
    const subject = reviewSubjectOf(a.row);
    const w = out.get(subject) ?? { subject, brand: a.row.brandSlug, creator: a.row.creatorEmail.toLowerCase(), week: a.row.week, awaiting: [] };
    w.awaiting.push(a);
    out.set(subject, w);
  }
  return out;
}

/** The creators with late work, by card subject: every `overdue` action, scripts and footage alike. */
export function lateCreators(actions: readonly OperationsAction[]): Map<string, LateCreator> {
  const out = new Map<string, LateCreator>();
  for (const a of actions) {
    if (a.reason !== 'overdue' || a.dueOn === null) continue;
    const subject = overdueSubjectOf(a.row);
    const c = out.get(subject) ?? { subject, brand: a.row.brandSlug, creator: a.row.creatorEmail.toLowerCase(), late: [] };
    c.late.push(a);
    out.set(subject, c);
  }
  return out;
}

const earliest = (days: readonly (string | null)[]): string | null =>
  days.reduce<string | null>((m, d) => (d !== null && (m === null || d < m) ? d : m), null);

/** The scripts a review card listed at its last pass, read back from its row. */
export function listedScripts(detail: CardDetail | null): ReviewScript[] {
  const v = detail?.scripts;
  if (!Array.isArray(v)) return [];
  const out: ReviewScript[] = [];
  for (const x of v as unknown[]) {
    if (x === null || typeof x !== 'object') continue;
    const s = x as Record<string, unknown>;
    if (typeof s.id !== 'string' || s.id === '') continue;
    out.push({
      id: s.id,
      title: typeof s.title === 'string' ? s.title : '',
      grade: typeof s.grade === 'number' && Number.isFinite(s.grade) ? s.grade : null,
      done: s.done === 1 ? 1 : 0,
    });
  }
  return out;
}

/**
 * A REVIEW CARD'S READING. The card lists the week's scripts sent to the AM:
 * those awaiting now, and those it listed before that have left the review
 * since (approved, sent to the brand, or back to the creator for edits: each
 * is the AM's review done). A listed script that no longer exists drops off.
 * Its counter is the reviewed of the listed; it is done when none awaits.
 *
 * `weekRows` is every script row of the week, whatever its stage; `reviewer`
 * names who moved the last of them out of the review, asked only when the
 * card closes.
 */
export function reviewReading(
  card: CardRow | null,
  awaiting: readonly OperationsAction[],
  weekRows: readonly WorkloadRow[],
  today: string,
  reviewer: (scriptIds: string[]) => { actor: string; at: number } | null,
): OwedReading {
  const byId = new Map<string, WorkloadRow>();
  for (const r of weekRows) if (r.scriptId) byId.set(r.scriptId, r);
  const waiting = new Set<string>();
  for (const a of awaiting) {
    if (!a.row.scriptId) continue;
    waiting.add(a.row.scriptId);
    if (!byId.has(a.row.scriptId)) byId.set(a.row.scriptId, a.row);
  }
  const ids = new Set<string>([...listedScripts(card?.detail ?? null).map((s) => s.id), ...waiting]);
  const rows = [...ids].map((id) => byId.get(id)).filter((r): r is WorkloadRow => r !== undefined);
  rows.sort((a, b) => a.videoNum - b.videoNum || (a.scriptId ?? '').localeCompare(b.scriptId ?? ''));
  const scripts: ReviewScript[] = rows.map((r) => ({
    id: r.scriptId ?? '',
    title: r.title,
    grade: r.latestScore,
    done: waiting.has(r.scriptId ?? '') ? 0 : 1,
  }));
  const owed = scripts.filter((s) => s.done === 0).length;
  const by = owed === 0 ? reviewer(scripts.map((s) => s.id)) : null;
  return {
    owed,
    dueOn: earliest(awaiting.map((a) => a.dueOn)) ?? card?.dueOn ?? today,
    target: scripts.length,
    doneN: scripts.length - owed,
    done: by === null ? null : { actor: by.actor, at: by.at, via: 'app' },
    workSince: null,
    extra: { week: rows[0]?.week ?? 0, scripts },
  };
}

/** What closed an overdue card for good, kept on it (`detail_json.closed`) so the page can say why it is done. */
export type OverdueClose = 'submitted' | 'moved' | 'cleared';

/** What the tick knows beyond the late work, to say why a card closed: the creator's rows for the brand, and who last moved their dates. */
export interface OverdueClosing {
  /** Every recorded row of the creator's for the card's brand, whatever its stage. */
  rows: readonly WorkloadRow[];
  /** The newest due-date change on the creator's weeks for the brand since `since` (ms): who, when, their first name. */
  movedBy: (since: number) => { actor: string; at: number; name: string } | null;
  /** The pass's time. */
  at: number;
}

const firstOf = (name: string): string => name.trim().split(/\s+/)[0] || name;

/** "2 scripts", "1 script and 2 videos". */
export function lateWhat(scripts: number, videos: number): string {
  const n = (k: number, w: string) => `${k} ${w}${k === 1 ? '' : 's'}`;
  return [scripts > 0 ? n(scripts, 'script') : '', videos > 0 ? n(videos, 'video') : ''].filter(Boolean).join(' and ');
}

/**
 * WHY A CARD WHOSE WORK IS NO LONGER LATE IS CLOSED, the last thing that
 * happened to the work: the creator submitted (the newest submission on or
 * after the card's oldest due day, scripts or footage), else someone moved the
 * creator's dates (the audit log names them), else nothing we can name. Who
 * is `done_by`, as the explanation of what happened, never as credit.
 */
function closeOf(card: CardRow, c: OverdueClosing): { done: OwedReading['done']; extra: CardDetail; note: string } {
  let day: string | null = null;
  let scripts = false;
  let footage = false;
  for (const r of c.rows) {
    for (const [d, isScript] of [[r.scriptSubmittedOn, true], [r.scriptResubmittedOn, true], [r.footageSubmittedOn, false], [r.footageResubmittedOn, false]] as const) {
      if (d === null || d < card.dueOn) continue;
      if (isScript) scripts = true;
      else footage = true;
      if (day === null || d > day) day = d;
    }
  }
  const creator = c.rows[0]?.creatorEmail.toLowerCase() ?? (card.subject.split('::')[0] ?? '').toLowerCase();
  const who = firstOf(c.rows[0]?.creator ?? creator);
  if (day !== null) {
    const what = scripts && footage ? 'scripts and footage' : footage ? 'footage' : 'scripts';
    return {
      done: { actor: creator || null, at: c.at, via: 'app' },
      extra: { scriptsLate: 0, videosLate: 0, weeks: 0, closed: 'submitted' satisfies OverdueClose, closedWhat: what, closedOn: day },
      note: `closed: ${who} submitted the ${what}`,
    };
  }
  const moved = c.movedBy(card.createdAt);
  if (moved !== null) {
    return {
      done: { actor: moved.actor, at: moved.at, via: 'app' },
      extra: { scriptsLate: 0, videosLate: 0, weeks: 0, closed: 'moved' satisfies OverdueClose, closedOn: operationsDayOf(moved.at) },
      note: `closed: due dates moved by ${moved.name}`,
    };
  }
  return { done: null, extra: { scriptsLate: 0, videosLate: 0, weeks: 0, closed: 'cleared' satisfies OverdueClose }, note: `closed: nothing of ${who}'s is late any more` };
}

/**
 * AN OVERDUE CARD'S READING: the creator's late deliverables for the brand,
 * every week and both ladders. Due on the oldest late due date, so the card
 * ages into the late sections; done when nothing is late, closed by what
 * ended the work (`closeOf`). Each move's history line says why: "opened: 1
 * day late (2 scripts)", "reopened: 2 days late (2 scripts)", "closed: Jay
 * submitted the scripts".
 *
 *   scriptsLate / videosLate   how many of each are late
 *   weeks                      over how many weeks
 */
export function overdueReading(card: CardRow | null, late: readonly OperationsAction[], today: string, closing: OverdueClosing | null = null): OwedReading {
  const scripts = late.filter((a) => a.ladder === 'script').length;
  const videos = late.filter((a) => a.ladder === 'footage').length;
  const weeks = new Set(late.map((a) => a.row.week)).size;
  // A week becomes late the day after it was due: that is when its work began, for the archive rule.
  const since = late.reduce<number | null>((m, a) => {
    if (a.dueOn === null) return m;
    const t = operationsDayStart(addDaysUtc(a.dueOn, 1));
    return m === null || t > m ? t : m;
  }, null);
  const dueOn = earliest(late.map((a) => a.dueOn)) ?? card?.dueOn ?? today;
  if (late.length === 0 && card !== null && closing !== null) {
    const c = closeOf(card, closing);
    return { owed: 0, dueOn, target: null, doneN: null, done: c.done, workSince: null, extra: c.extra, notes: { closed: c.note } };
  }
  const lateDays = Math.max(0, daysBetweenUtc(dueOn, today));
  const how = `${lateDays} day${lateDays === 1 ? '' : 's'} late (${lateWhat(scripts, videos)})`;
  return {
    owed: late.length,
    dueOn,
    target: null,
    doneN: null,
    done: null,
    workSince: since,
    extra: { scriptsLate: scripts, videosLate: videos, weeks },
    notes: { made: `opened: ${how}`, reopened: `reopened: ${how}`, back: how },
  };
}
